import { FilterBase } from "@components/commerce/product-listing/filters/common/filter/FilterBase";
import { SizeSwitcherDropdown } from "@components/commerce/product-listing/filters/common/size-switcher/SizeSwitcher";
import { FiltersContext } from "@components/commerce/product-listing/filters/context/FiltersContext";
import { SizeGuide } from "@components/ui/size-guide-modal/SizeGuide";
import { AnalyticsClient } from "@lib/analytics/AnalyticsClient";
import type { SizeLocales } from "@lib/constants/filtersConstants";
import { FEMALE, MALE } from "@lib/constants/personCharacteristics";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { useStore } from "@store/index";
import { useFilterStore } from "@store/filterStore";
import { Button } from "@ui/components/forms/button/Button";
import { Box } from "@ui/components/layout/box/Box";
import { Flex } from "@ui/components/layout/flex/Flex";
import { SelectDropdownItem } from "@ui/components/overlay/select-dropdown/SelectDropdown";
import type { RefinementListItem } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import {
    type Dispatch,
    memo,
    type SetStateAction,
    useCallback,
    useContext,
    useEffect,
} from "react";
import { useRefinementList } from "react-instantsearch";
import { styles } from "./SizeToolbarFilterStyles";

export const SizeToolbarFilter = memo(
    ({
        filterName,
        isToolbar,
        isLoading,
    }: {
        filterName: string;
        isToolbar: boolean;
        isLoading?: boolean;
    }) => {
        // Static data from main store
        const initialFilters = useStore((state) => state.data.products?.initialFilters);
        const setLastLoadedPage = useStore((state) => state.setLastLoadedPage);
        const defaultShoeSize = useStore((state) => state.data.me.globalShoeSizeConvention);
        const setDefaultShoeSize = useStore((state) => state.setGlobalShoeSizeConvention);

        // Static values from context
        const { isMenPage } = useContext(FiltersContext);

        // Dynamic filter store actions (no state subscriptions to avoid rerenders)
        const setFilterItems = useFilterStore((state) => state.setFilterItems);

        // Algolia refinement hook
        const { items, refine } = useRefinementList({
            attribute: `fullSizes.${defaultShoeSize.toLowerCase()}`,
            operator: "or",
            sortBy: ["name"],
            limit: 100,
        });

        // Update store with current facet data whenever items or refine function changes
        useEffect(() => {
            // Get current facets from store without subscription
            const currentFacets = useFilterStore.getState().facets;

            // Update store with new facet data
            setFilterItems({
                ...currentFacets,
                [filterName]: { items, refine },
            });
        }, [items, refine, filterName, setFilterItems]);

        let timerId = undefined;

        const applyRefinement = useCallback(
            (item: string) => {
                if (timerId) {
                    clearTimeout(timerId);
                } else {
                    timerId = setTimeout(() => {
                        AnalyticsClient.listFilterApplied(
                            filterName,
                            item,
                            `fullSizes.${defaultShoeSize.toLowerCase()}`
                        );
                    }, 1000);
                }
                scrollTo({ top: 0 });
                setLastLoadedPage(1);
                refine(item);
            },
            [refine, filterName, defaultShoeSize, setLastLoadedPage]
        );

        if (!isToolbar) {
            return <></>;
        }

        // Optimized size locale switcher
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const switchSizeLocale = useCallback(
            (newSizeLocale: SizeLocales) => {
                items.filter((it) => it.isRefined).forEach((it) => refine(it.value));
                scrollTo({ top: 0 });
                setLastLoadedPage(1);
                setDefaultShoeSize(newSizeLocale);
            },
            [items, refine, setLastLoadedPage, setDefaultShoeSize]
        );

        const dropdownItems = (
            <Box sx={styles.itemsWrapper}>
                <SizeSection
                    sizesList={initialFilters[`Size.${defaultShoeSize}`] as RefinementListItem[]}
                    setSizeLocale={switchSizeLocale}
                    sizeLocale={defaultShoeSize}
                    items={items}
                    refine={applyRefinement}
                />
                <Flex width="96%" margin="auto" justifyContent={"flex-start"}>
                    <SizeGuide
                        isToolbar={true}
                        gender={isMenPage ? MALE : FEMALE}
                        type={ModalTypes.globalSizeGuide}
                    />
                </Flex>
            </Box>
        );

        return (
            <FilterBase
                isLoading={isLoading}
                dropdownItems={dropdownItems}
                filterName={filterName}
                isToolbar={isToolbar}
            />
        );
    }
);

SizeToolbarFilter.displayName = "SizeToolbarFilter";

interface SizeSectionProps {
    sizesList?: RefinementListItem[];
    items: RefinementListItem[];
    refine: (value: string) => void;
    sizeLocale: SizeLocales;
    setSizeLocale: Dispatch<SetStateAction<SizeLocales>>;
}

const SizeSection = memo(
    ({ sizesList = [], items, refine, sizeLocale, setSizeLocale }: SizeSectionProps) => {
        return (
            <Flex
                sx={{
                    ...styles.sizeSectionMainContainer,
                }}
            >
                {sizesList.length ? (
                    <Box sx={styles.sectionWrapper}>
                        <SizeSwitcherDropdown
                            sizeLocale={sizeLocale}
                            setSizeLocale={(label) => {
                                setSizeLocale(label);
                            }}
                        />
                        <Box
                            sx={styles.sizesContainer}
                            overflowY="auto"
                            maxHeight={["70dvh", "63vh"]}
                        >
                            {[...sizesList]
                                .sort((a, b) => {
                                    return parseInt(a.label) - parseInt(b.label);
                                })
                                .map((item) => {
                                    const dynamicItem = items.find((it) => it.value === item.value);
                                    return (
                                        <SelectDropdownItem
                                            key={item.label}
                                            as={Box}
                                            sx={{
                                                ...styles.selectDropdownItem,
                                            }}
                                            isDisabled={!dynamicItem}
                                            onClick={() => {
                                                refine(item.value);
                                            }}
                                        >
                                            <Button
                                                key={item.label}
                                                sx={{
                                                    ...styles.itemButton,
                                                    _disabled: {
                                                        bg: "rgba(238, 238, 238, .3)",
                                                        color: "rgba(0, 0, 0, .6)",
                                                    },
                                                }}
                                                size="sm"
                                                fontWeight="bold"
                                                isDisabled={!dynamicItem}
                                                variant={
                                                    dynamicItem?.isRefined ? "solid" : "standard"
                                                }
                                            >
                                                <span>{item.label}</span>
                                            </Button>
                                        </SelectDropdownItem>
                                    );
                                })}
                        </Box>
                    </Box>
                ) : null}
            </Flex>
        );
    }
);

SizeSection.displayName = "SizeSection";
